import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

const STATUS_CLASS = {
  draft: "muted",
  proposed: "warn",
  accepted: "good",
  declined: "bad",
  active: "good",
  completed: "good",
  cancelled: "muted",
};

function formatAmount(cents, currency) {
  if (!cents) return "—";
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: currency || "CAD" });
}

function isoDate(offsetMonths) {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMonths);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function partyLabel(person) {
  if (!person?.name) return "";
  return person.company ? `${person.name} · ${person.company}` : person.name;
}

function IsoDateField({ value, onChange, testid }) {
  return (
    <input
      type="text"
      inputMode="numeric"
      required
      placeholder="YYYY-MM-DD"
      pattern="\d{4}-\d{2}-\d{2}"
      maxLength={10}
      title="YYYY-MM-DD"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^\d-]/g, "").slice(0, 10))}
      data-testid={testid}
    />
  );
}

const EMPTY_FORM = {
  founder_id: "",
  advisor_id: "",
  title: "",
  scope: "",
  currency: "CAD",
  total_amount_cents: 0,
  start_date: isoDate(0),
  end_date: isoDate(3),
  meeting_duration_min: 60,
  meeting_frequency: "weekly",
  confirm_required: false,
};

export default function Mandates() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const canCreate = user?.role === "accelerator";
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [connections, setConnections] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [titleTouched, setTitleTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/mandates").then((r) => setItems(r.data)).catch((e) => {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setItems([]);
    });
  }, []);

  useEffect(() => {
    if (!canCreate) return;
    api.get("/community").then((r) => {
      const buckets = r.data.mine || {};
      setConnections([
        ...(buckets.entrepreneur || []).map((c) => ({ ...c, _role: "entrepreneur" })),
        ...(buckets.advisor || []).map((c) => ({ ...c, _role: "advisor" })),
      ]);
    }).catch(() => setConnections([]));
  }, [canCreate]);

  if (!user) return <div className="tb-loading">{t("common.loading")}</div>;
  if (!["entrepreneur", "advisor", "admin", "accelerator"].includes(user.role)) {
    return (
      <div className="tb-page">
        <h1 className="tb-h1">{t("mandates.title")}</h1>
        <p className="tb-muted">{t("mandates.advisorEntrepreneurOnly")}</p>
      </div>
    );
  }
  if (items === null) return <div className="tb-loading">{t("common.loading")}</div>;

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/mandates", {
        title: form.title,
        scope: form.scope,
        currency: form.currency,
        total_amount_cents: Number(form.total_amount_cents) || 0,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        meeting_duration_min: Number(form.meeting_duration_min) || 60,
        meeting_frequency: form.meeting_frequency || "weekly",
        confirm_required: !!form.confirm_required,
        milestones: [],
        founder_id: form.founder_id,
        advisor_id: form.advisor_id,
      });
      const listed = await api.get("/mandates");
      setItems(listed.data || []);
      setCreating(false);
      setTitleTouched(false);
      setForm({ ...EMPTY_FORM, start_date: isoDate(0), end_date: isoDate(3) });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const roleBadge = (m) => {
    if (user.role === "accelerator") {
      const names = [partyLabel(m.founder), partyLabel(m.advisor)].filter(Boolean).join(" ↔ ");
      return names || t("mandates.youAreAccelerator");
    }
    const mine = m.advisor_id === user.id ? t("mandates.youAreAdvisor") : t("mandates.youAreFounder");
    const other = partyLabel(m.advisor_id === user.id ? m.founder : m.advisor);
    return other ? `${mine} · ${other}` : mine;
  };

  const founders = connections.filter((c) => c._role === "entrepreneur" && !c.team_owner_id);
  const advisors = connections.filter((c) => c._role === "advisor");

  const chooseParty = (patch) => {
    const next = { ...form, ...patch };
    if (!titleTouched) {
      const founder = founders.find((c) => c.id === next.founder_id);
      const advisor = advisors.find((c) => c.id === next.advisor_id);
      if (founder && advisor) next.title = `${advisor.name} · ${founder.name}`;
    }
    setForm(next);
  };

  return (
    <div className="tb-page">
      <Link to="/app/bookings" className="tb-muted" data-testid="mandates-back" style={{ fontSize: "0.85rem" }}>← {t("common.back")}</Link>
      <h1 className="tb-h1" data-testid="mandates-heading">{t("mandates.title")}</h1>
      <p className="tb-muted" style={{ maxWidth: 760, marginBottom: 16 }}>
        {canCreate ? t("mandates.subtitle") : t("mandates.notReady")}
      </p>
      {err && <div className="tb-form-error" style={{ marginBottom: 12 }}>{err}</div>}

      {canCreate && !creating && (
        <button className="tb-primary-btn" onClick={() => setCreating(true)} data-testid="mandate-new-btn" style={{ marginBottom: 16 }}>
          + {t("mandates.newMandate")}
        </button>
      )}

      {canCreate && creating && (
        <form onSubmit={create} className="tb-block" style={{ marginBottom: 24 }} data-testid="mandate-create-form">
          <h3 style={{ marginTop: 0 }}>{t("mandates.newMandate")}</h3>
          <div>
            <label>{t("mandates.entrepreneur")}</label>
            <select required value={form.founder_id} onChange={(e) => chooseParty({ founder_id: e.target.value })} data-testid="mandate-founder">
              <option value="">— {t("mandates.pickCounterparty")} —</option>
              {founders.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>
              ))}
            </select>
            <label>{t("mandates.advisor")}</label>
            <select required value={form.advisor_id} onChange={(e) => chooseParty({ advisor_id: e.target.value })} data-testid="mandate-advisor">
              <option value="">— {t("mandates.pickCounterparty")} —</option>
              {advisors.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>
              ))}
            </select>
            {connections.length === 0 && (
              <div className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 6 }}>
                {t("mandates.noConnections")} <Link to="/app/community">{t("nav.community")}</Link>
              </div>
            )}
          </div>
          <div className="mandate-section" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label>{t("mandates.duration")}</label>
              <input type="number" min="15" max="180" step="15" required value={form.meeting_duration_min} onChange={(e) => setForm({ ...form, meeting_duration_min: Number(e.target.value) || 60 })} data-testid="mandate-duration" />
            </div>
            <div>
              <label>{t("mandates.frequency")}</label>
              <select value={form.meeting_frequency} onChange={(e) => setForm({ ...form, meeting_frequency: e.target.value })} data-testid="mandate-frequency">
                <option value="weekly">{t("mandates.freq.weekly")}</option>
                <option value="biweekly">{t("mandates.freq.biweekly")}</option>
                <option value="monthly">{t("mandates.freq.monthly")}</option>
              </select>
            </div>
          </div>
          <div className="mandate-section">
            <label>{t("mandates.titleLabel")}</label>
            <input type="text" required value={form.title} onChange={(e) => { setTitleTouched(true); setForm({ ...form, title: e.target.value }); }} placeholder={t("mandates.titlePlaceholder")} data-testid="mandate-title" />
          </div>
          <div className="mandate-section">
            <label>{t("mandates.scope")}</label>
            <textarea rows={4} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} placeholder={t("mandates.scopePlaceholder")} data-testid="mandate-scope" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div>
              <label>{t("mandates.currency")}</label>
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="mandate-currency">
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div>
              <label>{t("mandates.cost")}</label>
              <input type="number" min="0" step="100" value={Math.round((form.total_amount_cents || 0) / 100)} onChange={(e) => setForm({ ...form, total_amount_cents: (Number(e.target.value) || 0) * 100 })} data-testid="mandate-amount" />
            </div>
          </div>
          <div className="mandate-section" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label>{t("mandates.startDate")}</label>
              <IsoDateField value={form.start_date} onChange={(start_date) => setForm({ ...form, start_date })} testid="mandate-start" />
            </div>
            <div>
              <label>{t("mandates.endDate")}</label>
              <IsoDateField value={form.end_date} onChange={(end_date) => setForm({ ...form, end_date })} testid="mandate-end" />
            </div>
          </div>
          <label className="mandate-confirm">
            <input type="checkbox" checked={form.confirm_required} onChange={(e) => setForm({ ...form, confirm_required: e.target.checked })} data-testid="mandate-confirm" />
            <span>
              {t("mandates.confirmRequest")}
              <div className="tb-muted" style={{ fontSize: "0.82rem", fontWeight: 400 }}>{t("mandates.confirmHint")}</div>
            </span>
          </label>
          <div className="mandate-actions" style={{ display: "flex", gap: 8 }}>
            <button type="submit" className="tb-primary-btn" disabled={busy} data-testid="mandate-create-submit">
              {busy ? t("auth.loading") : t("mandates.send")}
            </button>
            <button type="button" className="tb-ghost-btn" onClick={() => navigate("/app/bookings")}>{t("book.cancel")}</button>
          </div>
        </form>
      )}

      {(() => {
        const isPast = (m) => {
          if (["completed", "cancelled", "declined"].includes(m.status)) return true;
          if (m.end_date && /^\d{4}-\d{2}-\d{2}$/.test(m.end_date)) {
            const end = new Date(`${m.end_date}T23:59:59`);
            return !Number.isNaN(end.getTime()) && end.getTime() < Date.now();
          }
          return false;
        };
        const byRecent = (a, b) => new Date(b.created_at || b.start_date || 0) - new Date(a.created_at || a.start_date || 0);
        const active = items.filter((m) => !isPast(m)).sort(byRecent);
        const past = items.filter(isPast).sort(byRecent);
        const grid = (list, testid) => (
          <div className="tb-card-grid" data-testid={testid}>
            {list.map((m) => (
              <Link key={m.id} to={`/app/mandates/${m.id}`} className="tb-block" style={{ textDecoration: "none", color: "inherit" }} data-testid={`mandate-card-${m.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                  <strong style={{ fontSize: "1rem" }}>{m.title}</strong>
                  <span className={`tb-badge ${STATUS_CLASS[m.status] || "muted"}`}>{t(`mandates.status.${m.status}`)}</span>
                </div>
                <div className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 6 }}>{roleBadge(m)}</div>
                <div className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 4 }} data-testid={`mandate-dates-${m.id}`}>
                  {t("mandates.startDate")}: {m.start_date || "—"} · {t("mandates.endDate")}: {m.end_date || "—"}
                </div>
                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", fontSize: "0.85rem" }}>
                  <span>{formatAmount(m.total_amount_cents, m.currency)}</span>
                  <span className="tb-muted">{(m.milestones || []).length} {t("mandates.milestones")}</span>
                </div>
              </Link>
            ))}
          </div>
        );
        return (
          <>
            <h2 className="tb-h2" data-testid="heading-active-mandates">{t("dash.activeMandates")}</h2>
            {active.length === 0 ? (
              <p className="tb-muted" data-testid="no-active-mandates">{t("dash.noActiveMandates")}</p>
            ) : grid(active, "active-mandates-grid")}
            <h2 className="tb-h2" data-testid="heading-past-mandates">{t("dash.pastMandates")}</h2>
            {past.length === 0 ? (
              <p className="tb-muted" data-testid="no-past-mandates">{t("dash.noPastMandates")}</p>
            ) : grid(past, "past-mandates-grid")}
          </>
        );
      })()}
    </div>
  );
}
